// Daily survival: the base has to feed everyone each day or people start to
// starve. Food recovered on supply runs banks to the base pantry as nutrition units
// (items.mjs); each day the crew consumes a quota, and a shortfall makes people
// hungry — then, if still unfed the next day, dead. This is the demand side
// that gives scavenged food a point (the "so what" the loop was missing).
//
// Pure functions, no state: campaign-v4.mjs owns the pantry + roster and calls
// these on day advance.

export const FOOD_PER_SURVIVOR = 2; // "you + one dependent back home" per living survivor

// Total food the crew needs for a day. Shrinks as the roster shrinks, so
// losing people eases the quota (grim, but it keeps the pressure proportional).
export function dailyQuota(livingSurvivorCount, {
  foodPerSurvivor = FOOD_PER_SURVIVOR,
  foodConsumptionMultiplier = 1,
} = {}) {
  const multiplier = Number.isFinite(foodConsumptionMultiplier) && foodConsumptionMultiplier > 0
    ? foodConsumptionMultiplier
    : 1;
  return roundFoodCost(Math.max(0, livingSurvivorCount) * foodPerSurvivor * multiplier);
}

// Resolve one day's feeding against the pantry. Already-hungry survivors are
// fed FIRST — scarce food should pull the at-risk back from the brink before it
// tops up the healthy — so a partial pantry still does the most good. A
// survivor who can't be fed goes hungry; one who was ALREADY hungry and still
// can't be fed starves.
//
// survivors: [{ id, hunger }] (living only). Returns the outcome; no mutation.
export function resolveDailyFeeding(pantry, survivors, {
  foodPerSurvivor = FOOD_PER_SURVIVOR,
  foodConsumptionMultiplier = 1,
} = {}) {
  const effectiveFoodPerSurvivor = roundFoodCost(foodPerSurvivor * (
    Number.isFinite(foodConsumptionMultiplier) && foodConsumptionMultiplier > 0 ? foodConsumptionMultiplier : 1
  ));
  const order = [...survivors].sort((a, b) => (b.hunger ?? 0) - (a.hunger ?? 0));
  let available = Math.max(0, pantry);
  const fed = [];
  const nowHungry = [];
  const starved = [];
  for (const survivor of order) {
    if (available >= effectiveFoodPerSurvivor) {
      available -= effectiveFoodPerSurvivor;
      fed.push(survivor.id);
    } else if ((survivor.hunger ?? 0) >= 1) {
      starved.push(survivor.id);
    } else {
      nowHungry.push(survivor.id);
    }
  }
  return { pantryAfter: roundFoodCost(available), fed, nowHungry, starved };
}

function roundFoodCost(value) {
  return Number(value.toFixed(2));
}
